import React from "react";
import { Card, Flex, Heading, Link, Stack, Table, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { LuArrowRight } from "react-icons/lu";
import { STRIPE_CUSTOMER_PORTAL } from "../../helpers/GlobalStateProvider";
import { openPortalOnClick } from "../../helpers/stripePortal";
import { getPlanForTier } from "./planData";
import type { PaymentRecord } from "./types";

interface SubscriberSectionProps {
  stripeTier: number;
  tier: number;
  onLegacyPlan: boolean;
  credits: number;
  billingInterval?: string | null;
  paidThrough?: string | null;
  paymentHistory: PaymentRecord[];
}

export const SubscriberSection: React.FC<SubscriberSectionProps> = ({
  stripeTier,
  tier,
  onLegacyPlan,
  credits,
  billingInterval,
  paidThrough,
  paymentHistory,
}) => (
  <Flex gap={6} flexDir={{ base: "column", md: "row" }} width="full">
    {/* Left: Subscription Details */}
    <Card.Root flex={1}>
      <Card.Header>
        <Heading size="md" fontWeight="bold" fontSize="20px">
          Subscription Details
        </Heading>
      </Card.Header>
      <Card.Body>
        <Stack gap={4}>
          <Flex justifyContent="space-between" alignItems="center" height="32px">
            <Text fontSize="md" color="#52525b">Your current plan</Text>
            <Text fontSize="md" fontWeight="bold">{getPlanForTier(stripeTier || tier)}</Text>
          </Flex>
          {onLegacyPlan && (
            <Text fontSize="xs" color="#a1a1aa">
              You are on a legacy plan. Manage your subscription or upgrade to a new plan below.
            </Text>
          )}
          <Flex justifyContent="space-between" alignItems="center" height="32px">
            <Text fontSize="md" color="#52525b">Credit limit</Text>
            <Text fontSize="md" fontWeight="bold">{credits.toLocaleString()} credits</Text>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center" height="32px">
            <Text fontSize="md" color="#52525b">Payment method</Text>
            <Text fontSize="md" fontWeight="bold">
              Credit card
            </Text>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center" height="32px">
            <Text fontSize="md" color="#52525b">Renewal frequency</Text>
            <Text fontSize="md" fontWeight="bold">
              {billingInterval === "year" ? "Annual" : "Monthly"}
            </Text>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center" height="32px">
            <Text fontSize="md" color="#52525b">Next renewal date</Text>
            <Text fontSize="md" fontWeight="bold">
              {paidThrough
                ? new Date(paidThrough).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
                : "\u2014"}
            </Text>
          </Flex>
        </Stack>
      </Card.Body>
      <Card.Footer>
        <Button variant="solid" colorPalette="gray" width="full" asChild>
          <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
            Manage Subscription <LuArrowRight />
          </Link>
        </Button>
      </Card.Footer>
    </Card.Root>

    {/* Right: Payment History */}
    <Card.Root flex={1}>
      <Card.Header>
        <Heading size="md" fontWeight="bold" fontSize="20px">
          Payment History
        </Heading>
      </Card.Header>
      <Card.Body>
        {paymentHistory.length > 0 ? (
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Date</Table.ColumnHeader>
                <Table.ColumnHeader>Description</Table.ColumnHeader>
                <Table.ColumnHeader>Amount</Table.ColumnHeader>
                <Table.ColumnHeader></Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paymentHistory.slice(0, 5).map((payment, idx) => {
                const isPlanChange = payment.planChange === true;
                const isTrialActivation = payment.amount === 0 && !isPlanChange;
                const description = isPlanChange
                  ? `Plan changed to ${payment.tierName || "new plan"}`
                  : isTrialActivation
                    ? `Trial started${payment.tierName ? ` \u2014 ${payment.tierName}` : ""}`
                    : payment.tierName || "Subscription";
                return (
                  <Table.Row key={payment.invoiceNumber || idx}>
                    <Table.Cell>
                      {new Date(payment.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" color={isTrialActivation || isPlanChange ? "fg.muted" : undefined}>
                        {description}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {isTrialActivation || (isPlanChange && payment.amount === 0)
                        ? <Text fontSize="sm" color="fg.muted">{"\u2014"}</Text>
                        : `$${(payment.amount / 100).toFixed(2)}`}
                    </Table.Cell>
                    <Table.Cell>
                      {payment.invoiceUrl && (() => {
                        const isValidInvoiceUrl =
                          typeof payment.invoiceUrl === "string" &&
                          payment.invoiceUrl.startsWith("https://") &&
                          /^https:\/\/([a-z0-9-]+\.)*stripe\.com(\/|$)/i.test(payment.invoiceUrl);
                        return isValidInvoiceUrl ? (
                          <Link href={payment.invoiceUrl} target="_blank" rel="noopener noreferrer">
                            <LuArrowRight />
                          </Link>
                        ) : (
                          <Text fontSize="xs" color="fg.muted">Invoice</Text>
                        );
                      })()}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        ) : (
          <Text fontSize="sm" color="fg.muted">No payment history available.</Text>
        )}
      </Card.Body>
      <Card.Footer>
        <Button variant="solid" colorPalette="gray" width="full" asChild>
          <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
            View Billing History <LuArrowRight />
          </Link>
        </Button>
      </Card.Footer>
    </Card.Root>
  </Flex>
);
